import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate, useParams } from 'react-router-dom';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import { getVizTokens } from '../analytics/vizTokens';
import { downloadBlob, money, todayIso } from './shared';

/**
 * A payslip.
 *
 * Laid out as a statement someone could be handed: every line that moved the
 * figure is shown, in the order it applies, so the net amount can be checked by
 * eye rather than taken on trust.
 */
function PayslipDetail(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const navigate = useNavigate();
    const { payslipId } = useParams();

    const [slip, setSlip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payOpen, setPayOpen] = useState(false);
    const [paidOn, setPaidOn] = useState(todayIso());
    const [reference, setReference] = useState('');
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        // The list endpoint is the read path; a payslip is small enough that
        // filtering client-side beats adding a detail route.
        api.get('/staff/payslips/').then((res) => {
            const found = (res.data.payslips || [])
                .find((p) => String(p.id) === String(payslipId));
            setSlip(found || null);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));
    }, [payslipId, showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const confirmPay = () => {
        setSaving(true);
        api.post(`/staff/payslips/${payslipId}/pay/`, {
            paid_on: paidOn, reference,
        }).then((res) => {
            // The figure can move between drawing the draft up and paying it -
            // an advance taken during the hold does exactly that. The owner is
            // transferring a number they read off this screen, so if it changed
            // underneath them it must not slip past as a success message.
            const revised = res.data?.revised;
            if (revised) {
                showErrorMessage(
                    `Paid ${money(revised.now)}, not ${money(revised.was)} - `
                    + 'an advance or adjustment landed after this draft was '
                    + 'prepared. Check the payslip before transferring.');
            } else {
                showSuccessMessage('Marked as paid and added to expenses');
            }
            setPayOpen(false);
            load();
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setSaving(false));
    };

    const download = () => {
        setDownloading(true);
        api.get(`/staff/payslips/${payslipId}/document/`, { responseType: 'blob' })
            .then((res) => downloadBlob(res, 'payslip.pdf'))
            .catch(async (error) => {
                // The server refuses rather than handing over an unlocked file,
                // and its reason is the useful thing to say. The body arrives as
                // a blob because of responseType, so it has to be read back.
                let detail = '';
                try {
                    detail = JSON.parse(await error.response.data.text()).detail;
                } catch (ignored) { /* fall through to the generic message */ }
                showErrorMessage(detail || 'That payslip could not be produced.');
            })
            .finally(() => setDownloading(false));
    };

    const rows = slip ? [
        { label: 'Base salary', value: Number(slip.base_salary), kind: 'add' },
        Number(slip.unpaid_leave_deduction) > 0 && {
            label: `Unpaid leave (${slip.unpaid_leave_days} day${
                Number(slip.unpaid_leave_days) === 1 ? '' : 's'})`,
            value: -Number(slip.unpaid_leave_deduction), kind: 'sub' },
        Number(slip.other_deductions) > 0 && {
            label: 'Other deductions', value: -Number(slip.other_deductions), kind: 'sub' },
        Number(slip.advances_deducted) > 0 && {
            label: 'Advance recovered', value: -Number(slip.advances_deducted), kind: 'sub' },
        Number(slip.additions) > 0 && {
            label: 'Bonus / reimbursement', value: Number(slip.additions), kind: 'add' },
    ].filter(Boolean) : [];

    return (
        <AccessController require="owner">
            <Dialog open={payOpen} onClose={saving ? undefined : () => setPayOpen(false)}
                fullWidth maxWidth="xs" fullScreen={isMobile}>
                <DialogTitle>Mark as paid</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>{slip ? money(slip.net_payable) : ''} to {slip?.staff_name}</AlertTitle>
                        Once marked paid this payslip is frozen, any advances it
                        recovers are settled, and the amount is counted as a business
                        expense.
                    </Alert>
                    <TextField label="Paid on" type="date" fullWidth sx={{ mb: 2 }}
                        value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                    <TextField label="Payment reference (optional)" fullWidth
                        value={reference} onChange={(e) => setReference(e.target.value)}
                        placeholder="UPI / bank reference" />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPayOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={confirmPay} disabled={saving}>
                        {saving ? 'Saving...' : 'Confirm payment'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <IconButton aria-label="Back" onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Payslip</Typography>
                    {slip && (
                        <Chip size="small"
                            variant={slip.status === 'paid' ? 'filled' : 'outlined'}
                            color={slip.status === 'paid' ? 'success' : 'default'}
                            label={slip.status === 'paid' ? 'Paid' : 'Draft'} />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {slip && (
                        <>
                            <IconButton aria-label="Download payslip as a PDF"
                                onClick={download} disabled={downloading}>
                                <DownloadIcon />
                            </IconButton>
                            <IconButton aria-label="Print payslip" onClick={() => window.print()}>
                                <PrintIcon />
                            </IconButton>
                        </>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : !slip ? (
                    <Alert severity="error">This payslip could not be found.</Alert>
                ) : (
                    <>
                        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {slip.staff_name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 2 }}>
                                {slip.period_start} to {slip.period_end}
                            </Typography>

                            {rows.map((row) => (
                                <Box key={row.label} sx={{ display: 'flex',
                                    justifyContent: 'space-between', gap: 2, py: 0.75 }}>
                                    <Typography variant="body2">{row.label}</Typography>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500, whiteSpace: 'nowrap',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: row.kind === 'sub' ? tokens.critical : tokens.textPrimary,
                                    }}>
                                        {row.value < 0 ? '- ' : ''}{money(Math.abs(row.value))}
                                    </Typography>
                                </Box>
                            ))}

                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                alignItems: 'baseline', gap: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Net payable
                                </Typography>
                                <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
                                    {money(slip.net_payable)}
                                </Typography>
                            </Box>

                            {Number(slip.paid_leave_days) > 0 && (
                                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                                    Includes {slip.paid_leave_days} day(s) of paid leave,
                                    which do not reduce pay.
                                </Typography>
                            )}
                        </Paper>

                        {slip.payday && slip.status !== 'paid' && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Payable on <strong>{slip.payday}</strong>.
                            </Alert>
                        )}

                        {slip.status === 'paid' ? (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                Paid on {slip.paid_on}
                                {slip.payment_reference ? ` · ${slip.payment_reference}` : ''}.
                                Counted as a business expense on that date.
                            </Alert>
                        ) : (
                            <Box sx={{ mt: 2 }}>
                                <Button variant="contained" fullWidth={isMobile}
                                    onClick={() => setPayOpen(true)}>
                                    Mark as paid
                                </Button>
                                <Typography variant="caption"
                                    sx={{ display: 'block', mt: 1, color: tokens.textMuted }}>
                                    Still a draft. Recording more advances or leave for this
                                    period and preparing payslips again will update it.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </AccessController>
    );
}

export default PayslipDetail;
